import React, { useCallback, useEffect, useRef, useState } from 'react';

interface Position {
  x: number;
  y: number;
}

interface DragState {
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
  active: boolean;
}

const EDGE = 12;
const DRAG_THRESHOLD = 4;
const MAX_FLOATING_HEIGHT = 720;
const KEY_STEP = 24;

export function useDraggablePanel(panelRef: React.RefObject<HTMLElement>, width: number) {
  const [floating, setFloating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [floatingHeight, setFloatingHeight] = useState(MAX_FLOATING_HEIGHT);
  const drag = useRef<DragState | null>(null);

  const clamp = useCallback(
    (x: number, y: number): Position => ({
      x: Math.min(Math.max(EDGE, x), Math.max(EDGE, window.innerWidth - width - EDGE)),
      y: Math.min(Math.max(EDGE, y), Math.max(EDGE, window.innerHeight - 120)),
    }),
    [width],
  );

  const startFloating = useCallback(() => {
    setFloatingHeight(Math.min(window.innerHeight - EDGE * 2, MAX_FLOATING_HEIGHT));
    setFloating(true);
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('select, input, a, [data-no-drag]')) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    drag.current = {
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      active: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    if (!d.active) {
      if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < DRAG_THRESHOLD) return;
      d.active = true;
      setIsDragging(true);
      startFloating();
    }
    setPosition(clamp(e.clientX - d.offsetX, e.clientY - d.offsetY));
  };

  const endDrag = (e: React.PointerEvent<HTMLElement>) => {
    if (!drag.current) return;
    drag.current = null;
    setIsDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const nudge = (dx: number, dy: number) => {
    const rect = panelRef.current?.getBoundingClientRect();
    const base = floating ? position : { x: rect?.left ?? EDGE, y: rect?.top ?? EDGE };
    if (!floating) startFloating();
    setPosition(clamp(base.x + dx, base.y + dy));
  };

  const onHandleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const step = e.shiftKey ? KEY_STEP * 3 : KEY_STEP;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    if (moves[e.key]) {
      e.preventDefault();
      nudge(...moves[e.key]);
    } else if (e.key === 'Escape' && floating) {
      setFloating(false);
    }
  };

  const dock = useCallback(() => setFloating(false), []);

  useEffect(() => {
    document.body.style.cursor = isDragging ? 'grabbing' : '';
    document.body.style.userSelect = isDragging ? 'none' : '';
  }, [isDragging]);

  useEffect(() => {
    if (!floating) return;
    const onResize = () => setPosition((p) => clamp(p.x, p.y));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [floating, clamp]);

  return {
    floating,
    isDragging,
    position,
    floatingHeight,
    dock,
    onHandleKeyDown,
    dragHandleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
}
